export type OperationPlanTermSelection = 1 | 2 | 'all';

export type OperationPlanFooterRow = {
  kind: 'subtotal' | 'grandTotal';
  label: string;
  termNumber?: 1 | 2;
  totalInstructionalDays: number;
  totalHoursByColumnKey: Record<string, number>;
  vacationText?: string;
};

export type OperationPlanClassHourRow = {
  columnKey: string;
  gradeId: string;
  classLabel: string;
  gradeNumber: string;
  hours: number;
  adjusted: boolean;
};

export type OperationPlanWeekRow = {
  monthLabel: string;
  weekIndex: number;
  instructionalDayCount: number;
  weekdays: Array<{
    key: string;
    dateIso: string;
    inTerm: boolean;
    dayKind: string;
    dayNumber: string;
    fixedEvents: Array<{ name: string; colorHex: string; isGeneralMemo: boolean }>;
    fixedDayGeneralMemoOnly: boolean;
    fixedDayTextColor: string;
  }>;
  classHours: OperationPlanClassHourRow[];
  activityLines: Array<{
    dedupKey: string;
    sortIso: string;
    datePart: string;
    name: string;
    kind: 'calendar' | 'fixed' | 'shortday';
    colorHex?: string;
    isGeneralMemo?: boolean;
    tagCommon: boolean;
    gradeNums: number[];
  }>;
  weekActivityRemarkTexts: string[];
};

function formatIsoWithDots(iso: string): string {
  const trimmed = (iso ?? '').trim();
  if (!trimmed) return '';
  const parts = trimmed.split('-');
  if (parts.length !== 3) return trimmed;
  return `${parts[0]}.${parts[1]}.${parts[2]}`;
}

export function computeOperationPlanTotals(rows: OperationPlanWeekRow[]): {
  totalInstructionalDays: number;
  totalHoursByColumnKey: Record<string, number>;
} {
  const totalInstructionalDays = rows.reduce((sum, row) => sum + row.instructionalDayCount, 0);
  const totalHoursByColumnKey: Record<string, number> = {};
  rows.forEach((row) => {
    row.classHours.forEach((ch) => {
      totalHoursByColumnKey[ch.columnKey] = (totalHoursByColumnKey[ch.columnKey] ?? 0) + ch.hours;
    });
  });
  return { totalInstructionalDays, totalHoursByColumnKey };
}

export function mergeOperationPlanTotals(
  left: ReturnType<typeof computeOperationPlanTotals>,
  right: ReturnType<typeof computeOperationPlanTotals>
): ReturnType<typeof computeOperationPlanTotals> {
  const totalHoursByColumnKey: Record<string, number> = { ...left.totalHoursByColumnKey };
  Object.entries(right.totalHoursByColumnKey).forEach(([key, value]) => {
    totalHoursByColumnKey[key] = (totalHoursByColumnKey[key] ?? 0) + value;
  });
  return {
    totalInstructionalDays: left.totalInstructionalDays + right.totalInstructionalDays,
    totalHoursByColumnKey,
  };
}

type CalendarSectionSettings = {
  summerBreakStartsOn?: string | null;
  summerBreakEndsOn?: string | null;
  winterBreakStartsOn?: string | null;
  winterBreakEndsOn?: string | null;
};

export function getOperationPlanVacationText(
  termNumber: 1 | 2,
  settings: CalendarSectionSettings | null | undefined
): string {
  if (!settings) return '';
  if (termNumber === 1) {
    const from = (settings.summerBreakStartsOn ?? '').trim();
    const to = (settings.summerBreakEndsOn ?? '').trim();
    if (!from || !to) return '여름방학 기간:';
    return `여름방학 기간: ${formatIsoWithDots(from)}~${formatIsoWithDots(to)}`;
  }
  const from = (settings.winterBreakStartsOn ?? '').trim();
  const to = (settings.winterBreakEndsOn ?? '').trim();
  if (!from || !to) return '';
  return `겨울방학 기간: ${formatIsoWithDots(from)}~${formatIsoWithDots(to)}`;
}

export function buildOperationPlanExportSubtitle(params: {
  academicYear: number;
  termSelection: OperationPlanTermSelection;
  termMetaByNumber: Partial<Record<1 | 2, { startsOn: string; endsOn: string }>>;
}): string {
  const { academicYear, termSelection, termMetaByNumber } = params;
  if (termSelection === 'all') {
    const parts: string[] = [];
    const meta1 = termMetaByNumber[1];
    const meta2 = termMetaByNumber[2];
    if (meta1) {
      parts.push(`1학기(${formatIsoWithDots(meta1.startsOn)}~${formatIsoWithDots(meta1.endsOn)})`);
    }
    if (meta2) {
      parts.push(`2학기(${formatIsoWithDots(meta2.startsOn)}~${formatIsoWithDots(meta2.endsOn)})`);
    }
    return parts.length > 0 ? `${academicYear}학년도 ${parts.join(' · ')}` : `${academicYear}학년도`;
  }
  const meta = termMetaByNumber[termSelection];
  if (!meta) return `${academicYear}학년도`;
  return `${academicYear}학년도 ${termSelection}학기(${formatIsoWithDots(meta.startsOn)}~${formatIsoWithDots(meta.endsOn)})`;
}

export function getOperationPlanFooterRowClassName(footer: {
  kind: 'subtotal' | 'grandTotal';
  termNumber?: 1 | 2;
}): string {
  const classes = ['cp-annual-operation-plan-total-row'];
  if (footer.kind === 'subtotal') {
    classes.push('cp-annual-operation-plan-subtotal-row');
  }
  if (footer.termNumber === 1) {
    classes.push('cp-annual-operation-plan-footer-term1');
  } else if (footer.termNumber === 2) {
    classes.push('cp-annual-operation-plan-footer-term2');
  } else if (footer.kind === 'grandTotal') {
    classes.push('cp-annual-operation-plan-footer-grand-total');
  }
  return classes.join(' ');
}
